import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAt,
  faCircle,
  faClose,
  faGear,
  faPaperPlane,
  faPlus,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import ChatBubbleOutlineTwoToneIcon from "@mui/icons-material/ChatBubbleOutlineTwoTone";
import MarkEmailReadTwoToneIcon from "@mui/icons-material/MarkEmailReadTwoTone";
import InboxTwoToneIcon from "@mui/icons-material/InboxTwoTone";
import { courses, GeneralCourse } from "../../components/tutorial/courses";
import { Chip } from "../../components/Chip";
import { useUser } from "../../components/UserContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import React from "react";
import { getUserFacingError } from "../../utils/userFacingError";
import { FloatContainer } from "../../components/FloatContainer";
import { TextField } from "../../components/Field";
import { Button } from "../../components/Button";
import "./support.css";
import localforage from "localforage";
import { enterpriseAPI, supportAPI, RTA, sessionInfo } from "../..";

interface TicketSummary {
  id: string;
  subject: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  created_at: string;
  last_body?: string;
  last_author_type?: "customer" | "collaborator";
  last_message_at?: string;
  unread_count: number;
}

interface TicketMessage {
  id: string;
  ticket_id: string;
  author_type: "customer" | "collaborator";
  author_id?: string;
  body: string;
  created_at: string;
}

const STATUS_LABEL: Record<TicketSummary["status"], string> = {
  open: "Aberto",
  in_progress: "Em andamento",
  resolved: "Resolvido",
  closed: "Fechado",
};

const STATUS_CHIP_CLASS: Record<TicketSummary["status"], string> = {
  open: "ticketChipOpen",
  in_progress: "ticketChipInProgress",
  resolved: "ticketChipResolved",
  closed: "ticketChipClosed",
};

// Compact relative timestamp suitable for inbox rows ("2h", "3d", "Mar 5").
// Falls back to full date when the message is older than ~30 days so users
// still get a concrete reference.
function relativeTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = Date.now();
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function formatFullTimestamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Platform-aware modifier hint for the composer shortcut. Mac users see "Cmd",
// everyone else sees "Ctrl"; the keydown handler already accepts both.
const IS_MAC_PLATFORM =
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent || "");

// Backend caps a customer reply at 4000 chars; mirror it on the textarea so the
// user is stopped at the limit instead of eating a 400 after typing a wall.
const MAX_REPLY_LENGTH = 4000;

const TutorialSummary: React.FC<GeneralCourse> = (props) => {
  const { concludedTutorials, setAmaryCourse } = useUser();

  const TutorialState: React.FC = () => {
    if (concludedTutorials.includes(props.summary.id)) {
      return <Chip class="tutorialSummaryState tutorialSummaryStateCompleted">Concluído</Chip>;
    }

    return <Chip class="tutorialSummaryState tutorialSummaryStateNotStarted">Pendente</Chip>;
  };

  return (
    <div
      className="tutorialSummaryContainer"
      onClick={() => {
        localforage.setItem("inTutorial", true);
        setAmaryCourse({
          id: props.summary.id,
          currentChapter: 0,
        });
      }}
    >
      <TutorialState />
      <div className="tutorialSummaryPreview">{props.summary.preview}</div>
      <div className="tutorialSummaryInfo">
        <span className="tutorialSummaryInfoTitle">{props.summary.title}</span>
        <span className="tutorialSummaryInfoDesc">{props.summary.description}</span>
      </div>
    </div>
  );
};

// New-ticket form, retained from the original page but recast as a modal
// triggered by an explicit "Nova mensagem" button on the inbox.
const SupportEmailForm: React.FC<{
  onClose: () => void;
  onCreated?: (ticket?: TicketSummary) => void;
}> = ({ onClose, onCreated }) => {
  const [titleFieldLength, setTitleFieldLength] = React.useState(0);
  // Raw length drives the "caracteres restantes" counter; trimmed length gates
  // the submit so a whitespace-only body cannot enable a send that handleSend
  // would then silently drop.
  const [messageFieldLength, setMessageFieldLength] = React.useState(0);
  const [messageTrimmedLength, setMessageTrimmedLength] = React.useState(0);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState(false);
  const titleFieldRef = React.useRef<string>("");
  const messageFieldRef = React.useRef<HTMLTextAreaElement>(null);
  const maxMessageLength = 1600;
  // Gate the send button by content so an empty submit can never be a silent
  // no-op: both a subject and a body are required (both measured trimmed).
  const canSend = titleFieldLength > 0 && messageTrimmedLength > 0;

  async function handleSend() {
    const title = titleFieldRef.current.trim();
    const message = messageFieldRef.current?.value.trim() ?? "";
    if (!title || !message || sending) return;

    setSending(true);
    setError(null);

    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) {
      setError("Sessão expirada. Faça login novamente.");
      setSending(false);
      return;
    }

    // One idempotency key per submit. The backend dedups its outbox on this,
    // so a double click cannot open two tickets, and it no longer returns a
    // synchronous ticket id (the real id is minted async on the tartaro side).
    const clientEventId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      await enterpriseAPI.post(
        "/support-ticket",
        { subject: title, message, clientEventId },
        { headers: { "X-JWT": JWT } },
      );
      // The create is queued (200 {status:"queued"}) and carries no ticket id,
      // so the ticket is not openable yet. Show a NON-openable optimistic row
      // keyed on the client event id; the inbox reconciles it by subject once
      // the real ticket lands (see pollForCreated in Support). Never surface a
      // "temp-" id as openable, or clicking it 404s silently.
      const nowIso = new Date().toISOString();
      const optimisticTicket: TicketSummary = {
        id: `temp-${clientEventId}`,
        subject: title,
        status: "open",
        created_at: nowIso,
        last_body: message,
        last_author_type: "customer",
        last_message_at: nowIso,
        unread_count: 0,
      };
      setSent(true);
      onCreated?.(optimisticTicket);
      setTimeout(onClose, 1500);
    } catch (e: any) {
      setError(
        getUserFacingError(e, {
          context: "support",
          fallback: "Não foi possível enviar sua mensagem agora. Tente novamente em instantes.",
        }),
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <FloatContainer close={onClose}>
      <div id="supportEmailFormContainer">
        {sent ? (
          <span style={{ color: "var(--success-11)", fontWeight: 600, textAlign: "center" }}>
            Mensagem enviada com sucesso!
          </span>
        ) : (
          <>
            <TextField
              placeholder="Título do seu problema"
              onInput={(e: React.FormEvent<HTMLInputElement>) => {
                titleFieldRef.current = e.currentTarget.value;
                setTitleFieldLength(e.currentTarget.value.trim().length);
              }}
              maxLength={70}
              size="fill"
              icon={<FontAwesomeIcon icon={faAt} />}
            />
            <div className="supportEmailMessageBlock">
              <textarea
                ref={messageFieldRef}
                id="supportEmailMessageField"
                placeholder="Descreva seu problema aqui..."
                rows={15}
                maxLength={maxMessageLength}
                onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                  const value = e.currentTarget.value;
                  setMessageFieldLength(value.length);
                  setMessageTrimmedLength(value.trim().length);
                }}
              />
              <span id="supportEmailMessageLengthIndicator">
                {`${maxMessageLength - messageFieldLength} caracteres restantes`}
              </span>
            </div>
            {error && (
              <span style={{ color: "var(--danger-11)", fontSize: "var(--font-size-sm)" }}>
                {error}
              </span>
            )}
            <Button
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              iconSlot="end"
              class="supportEmailMessageSendButton"
              callback={handleSend}
              disabled={!canSend}
              loading={{ state: sending, text: "Enviando..." }}
            >
              Enviar mensagem
            </Button>
          </>
        )}
      </div>
    </FloatContainer>
  );
};

// Thread drawer: full message timeline for a single ticket plus reply input.
// Auto-scrolls to bottom on new content. Status is tracked in local state (not
// derived from the immutable ticket prop): the org-scoped RTA envelope carries
// the live status and updates the chip + composer, and a customer reply on a
// closed ticket optimistically reopens it.
const TicketThreadDrawer: React.FC<{
  ticket: TicketSummary;
  onClose: () => void;
  onReplied: () => void;
}> = ({ ticket, onClose, onReplied }) => {
  const [messages, setMessages] = React.useState<TicketMessage[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [status, setStatus] = React.useState<TicketSummary["status"]>(ticket.status);
  const [replyText, setReplyText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const fetchMessages = React.useCallback(async () => {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return;
    try {
      const res = await supportAPI.get(`/tickets/${ticket.id}/messages`, {
        headers: { "X-JWT": JWT },
      });
      setMessages(res.data?.messages || []);
      setLoadError(false);
    } catch (e) {
      console.error("Failed to load thread:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [ticket.id]);

  const retryLoad = React.useCallback(() => {
    setLoadError(false);
    setLoading(true);
    fetchMessages();
  }, [fetchMessages]);

  React.useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Subscribe to support RTA fan-out so collaborator replies / status
  // changes from tartaro show up live without manual reload. Org-scoped
  // event keyed off the JWT's org_id; the handler filters by ticketId
  // to ignore updates from other tickets in the same org.
  React.useEffect(() => {
    let alive = true;
    let eventName: string | null = null;
    const handler = (payload: any) => {
      if (!alive) return;
      if (payload?.ticketId && payload.ticketId !== ticket.id) return;
      // The org-scoped envelope carries the ticket's current status; reflect it
      // on the open drawer so the chip and composer track live changes.
      if (payload?.status) setStatus(payload.status as TicketSummary["status"]);
      fetchMessages();
    };
    sessionInfo().then((session) => {
      if (!alive || !session?.org_id) return;
      eventName = `supportTicketUpdate-${session.org_id}`;
      RTA.connect();
      RTA.on(eventName, handler);
    });
    return () => {
      alive = false;
      if (eventName) RTA.off(eventName, handler);
    };
  }, [ticket.id, fetchMessages]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleReply() {
    const body = replyText.trim();
    if (!body || sending) return;

    setSending(true);
    setError(null);

    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) {
      setError("Sessão expirada. Faça login novamente.");
      setSending(false);
      return;
    }

    // Optimistic append — UX feels native; reconciled when fetchMessages
    // re-runs and we pull the canonical timeline (server-assigned id).
    const tempID = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: tempID,
        ticket_id: ticket.id,
        author_type: "customer",
        body,
        created_at: new Date().toISOString(),
      },
    ]);
    setReplyText("");

    try {
      await supportAPI.post(
        `/tickets/${ticket.id}/reply`,
        { message: body },
        { headers: { "X-JWT": JWT } },
      );
      // A reply on a closed ticket reopens it on the backend
      // (ReopenTicketIfClosed). Reflect that locally so the chip and the
      // composer placeholder stop saying "Fechado" right away.
      setStatus((prev) => (prev === "closed" ? "open" : prev));
      await fetchMessages();
      onReplied();
    } catch (error) {
      setError(
        getUserFacingError(error, {
          context: "support",
          fallback:
            "Não foi possível enviar a resposta. O texto foi mantido para você tentar novamente.",
        }),
      );
      setReplyText(body);
      setMessages((prev) => prev.filter((m) => m.id !== tempID));
    } finally {
      setSending(false);
    }
  }

  return (
    <FloatContainer close={onClose} className="ticketThreadFloat">
      <div id="ticketThreadContainer">
        <div id="ticketThreadHeader">
          <div id="ticketThreadHeaderAvatar" aria-hidden>
            SP
          </div>
          <div id="ticketThreadHeaderInfo">
            <span id="ticketThreadSubject">{ticket.subject}</span>
            <span id="ticketThreadHeaderMeta">
              <Chip class={`ticketChip ${STATUS_CHIP_CLASS[status]}`}>{STATUS_LABEL[status]}</Chip>
            </span>
          </div>
          <button type="button" id="ticketThreadHeaderClose" aria-label="Fechar" onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
        <div id="ticketThreadMessages">
          {loading ? (
            <div id="ticketThreadLoading" aria-label="Carregando histórico">
              <div className="ticketSkeletonRow ticketSkeletonRow-collaborator">
                <div className="ticketSkeletonBubble" style={{ width: 180 }} />
              </div>
              <div className="ticketSkeletonRow ticketSkeletonRow-customer">
                <div className="ticketSkeletonBubble" style={{ width: 220 }} />
              </div>
              <div className="ticketSkeletonRow ticketSkeletonRow-collaborator">
                <div className="ticketSkeletonBubble" style={{ width: 140 }} />
              </div>
            </div>
          ) : loadError ? (
            <div id="ticketThreadError" role="alert">
              <span className="ticketThreadErrorTitle">
                Não foi possível carregar as mensagens.
              </span>
              <button type="button" className="ticketThreadRetryBtn" onClick={retryLoad}>
                <FontAwesomeIcon icon={faRotateRight} /> Tentar de novo
              </button>
            </div>
          ) : messages.length === 0 ? (
            <span id="ticketThreadEmpty">Sem mensagens ainda. Escreva a primeira abaixo.</span>
          ) : (
            messages.map((m, idx) => {
              // groupedTop = previous message is from the same author, so
              // this row continues a streak. Used to (a) tighten the gap
              // above and (b) skip the sharp top corner — the streak's
              // pointed corner only appears on the FIRST bubble of each
              // group, marking where the speaker took the turn.
              const prev = messages[idx - 1];
              const groupedTop = prev?.author_type === m.author_type;
              return (
                <div
                  key={m.id}
                  className={`ticketMessageRow ticketMessageRow-${m.author_type}${groupedTop ? " groupedTop" : ""}`}
                >
                  <div className="ticketMessageBubbleCol">
                    <div className="ticketMessageBubble">{m.body}</div>
                    <span className="ticketMessageTime">{formatFullTimestamp(m.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <div id="ticketThreadComposer">
          {error && (
            <span
              style={{
                color: "var(--danger-11)",
                fontSize: "var(--font-size-sm)",
              }}
            >
              {error}
            </span>
          )}
          <div id="ticketThreadComposerWrap">
            <textarea
              className="ticketThreadComposerField"
              placeholder={
                status === "closed" ? "Responder reabre o ticket..." : "Escreva sua resposta..."
              }
              rows={1}
              maxLength={MAX_REPLY_LENGTH}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleReply();
                }
              }}
            />
            <button
              type="button"
              className="ticketThreadSendBtn"
              aria-label="Enviar"
              onClick={handleReply}
              disabled={sending || replyText.trim().length === 0}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
          <div id="ticketThreadComposerHint">
            <span className="ticketThreadComposerHintKeys">
              <kbd>{IS_MAC_PLATFORM ? "Cmd" : "Ctrl"}</kbd> + <kbd>↵</kbd> para enviar
            </span>
            {replyText.length > MAX_REPLY_LENGTH - 400 && (
              <span
                className={`ticketThreadComposerCount${
                  replyText.length >= MAX_REPLY_LENGTH ? " ticketThreadComposerCountMax" : ""
                }`}
              >
                {replyText.length}/{MAX_REPLY_LENGTH}
              </span>
            )}
          </div>
        </div>
      </div>
    </FloatContainer>
  );
};

const TicketInboxRow: React.FC<{
  ticket: TicketSummary;
  onClick: () => void;
  pending?: boolean;
}> = ({ ticket, onClick, pending }) => {
  const previewAuthor =
    ticket.last_author_type === "collaborator"
      ? "Suporte"
      : ticket.last_author_type === "customer"
        ? "Você"
        : "";
  const hasMessages = !!ticket.last_body?.trim();
  const preview = hasMessages ? ticket.last_body!.trim() : "Sem mensagens ainda";
  const subjectDisplay = ticket.subject?.trim() || "Sem assunto";
  const subjectMissing = !ticket.subject?.trim();
  const unread = ticket.unread_count > 0;
  const IconComponent = unread ? ChatBubbleOutlineTwoToneIcon : MarkEmailReadTwoToneIcon;

  return (
    <button
      type="button"
      className={`ticketInboxRow ${unread ? "ticketInboxRowUnread" : ""}${
        pending ? " ticketInboxRowPending" : ""
      }`}
      onClick={onClick}
      disabled={pending}
      aria-busy={pending || undefined}
    >
      <div className="ticketInboxRowIcon" aria-hidden>
        <IconComponent />
      </div>
      <div className="ticketInboxRowBody">
        <span
          className={`ticketInboxRowSubject ${subjectMissing ? "ticketInboxRowSubjectMuted" : ""}`}
        >
          {subjectDisplay}
        </span>
        <span
          className={`ticketInboxRowPreview ${!hasMessages ? "ticketInboxRowPreviewEmpty" : ""}`}
        >
          {hasMessages && previewAuthor && (
            <span className="ticketInboxRowPreviewAuthor">{previewAuthor}:</span>
          )}
          <span className="ticketInboxRowPreviewBody">{preview}</span>
        </span>
      </div>
      <div className="ticketInboxRowAside">
        {pending ? (
          <span className="ticketInboxRowPendingLabel">
            <FontAwesomeIcon icon={faGear} spin /> Processando...
          </span>
        ) : (
          <>
            <Chip class={`ticketChip ${STATUS_CHIP_CLASS[ticket.status]}`}>
              {STATUS_LABEL[ticket.status]}
            </Chip>
            <span className="ticketInboxRowTime">
              {unread && (
                <span className="ticketInboxUnreadDot" aria-label="Não lido">
                  <FontAwesomeIcon icon={faCircle} />
                </span>
              )}
              {relativeTime(ticket.last_message_at || ticket.created_at)}
            </span>
          </>
        )}
      </div>
    </button>
  );
};

const SupportInboxSkeleton: React.FC = () => (
  <div className="ticketInboxSkeletonList" aria-hidden>
    {[0, 1, 2].map((i) => (
      <div className="ticketInboxRowSkeleton" key={i}>
        <div className="ticketInboxRowSkeletonIcon" />
        <div className="ticketInboxRowSkeletonBody">
          <div className="ticketInboxRowSkeletonLine ticketInboxRowSkeletonLineWide" />
          <div className="ticketInboxRowSkeletonLine ticketInboxRowSkeletonLineNarrow" />
        </div>
        <div className="ticketInboxRowSkeletonAside">
          <div className="ticketInboxRowSkeletonChip" />
          <div className="ticketInboxRowSkeletonTime" />
        </div>
      </div>
    ))}
  </div>
);

const SupportInboxEmpty: React.FC<{ onNewMessage: () => void }> = ({ onNewMessage }) => (
  <div className="supportInboxEmpty">
    <InboxTwoToneIcon className="supportInboxEmptyIcon" />
    <p className="supportInboxEmptyTitle">Sua caixa está vazia</p>
    <p className="supportInboxEmptyBody">
      Abra um ticket quando precisar de ajuda. Nossa equipe responde por aqui.
    </p>
    <Button
      icon={<FontAwesomeIcon icon={faPlus} />}
      iconSlot="start"
      class="supportInboxEmptyCTA"
      callback={onNewMessage}
    >
      Abrir primeiro ticket
    </Button>
  </div>
);

// Distinct from the empty state: a failed inbox load must not masquerade as
// "caixa vazia" (silent fallback). Offer an explicit retry.
const SupportInboxError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="supportInboxError" role="alert">
    <p className="supportInboxErrorTitle">Não foi possível carregar suas conversas</p>
    <p className="supportInboxErrorBody">
      Algo deu errado ao buscar seus tickets. Verifique sua conexão e tente de novo.
    </p>
    <Button
      icon={<FontAwesomeIcon icon={faRotateRight} />}
      iconSlot="start"
      class="supportInboxErrorCTA"
      callback={onRetry}
    >
      Tentar de novo
    </Button>
  </div>
);

export const Support: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [tickets, setTickets] = React.useState<TicketSummary[]>([]);
  const [loadingTickets, setLoadingTickets] = React.useState(true);
  const [ticketsError, setTicketsError] = React.useState(false);
  const [openTicket, setOpenTicket] = React.useState<TicketSummary | null>(null);
  // Ids of tickets inserted optimistically that the server has not confirmed
  // yet. Kept so a refetch that has not caught up does not wipe the row the
  // user just created.
  const optimisticIdsRef = React.useRef<Set<string>>(new Set());
  // Tutorials use desktop-only spotlight masks (see Tutorial.tsx); hide
  // the entry cards on mobile so users don't land on a broken experience.
  const isMobile = useMediaQuery("(max-width: 767px)");

  const fetchTickets = React.useCallback(async (): Promise<TicketSummary[] | null> => {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return null;
    try {
      const res = await supportAPI.get("/tickets", {
        headers: { "X-JWT": JWT },
      });
      const serverTickets: TicketSummary[] = res.data?.tickets || [];
      const serverIds = new Set(serverTickets.map((t) => t.id));
      const serverSubjects = new Set(serverTickets.map((t) => (t.subject || "").trim()));
      setTickets((prev) => {
        // Carry over any optimistic row the server has not reflected yet
        // (matched by id or subject). Drop it once the real one shows up.
        const keptOptimistic = prev.filter((t) => {
          if (!optimisticIdsRef.current.has(t.id)) return false;
          const reconciled = serverIds.has(t.id) || serverSubjects.has((t.subject || "").trim());
          if (reconciled) {
            optimisticIdsRef.current.delete(t.id);
            return false;
          }
          return true;
        });
        return [...keptOptimistic, ...serverTickets];
      });
      setTicketsError(false);
      return serverTickets;
    } catch (e) {
      // A load failure must not fall through to the empty state (silent
      // fallback). Flag it so the inbox shows an explicit retry instead.
      console.error("Failed to load support tickets:", e);
      setTicketsError(true);
      return null;
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  // After a create is queued, the real ticket lands asynchronously on the
  // tartaro side. Poll the inbox a few times over ~6s so the optimistic
  // "processando" row is swapped for the openable ticket without a manual
  // refresh. fetchTickets reconciles by subject on each pass.
  const pollForCreated = React.useCallback(
    async (tempId: string, subject: string) => {
      const target = (subject || "").trim();
      const delays = [1500, 2000, 2500];
      for (const wait of delays) {
        await new Promise((resolve) => setTimeout(resolve, wait));
        if (!optimisticIdsRef.current.has(tempId)) return;
        const server = await fetchTickets();
        if (server && server.some((t) => (t.subject || "").trim() === target)) return;
      }
    },
    [fetchTickets],
  );

  const handleTicketCreated = React.useCallback(
    (ticket?: TicketSummary) => {
      if (!ticket) {
        fetchTickets();
        return;
      }
      optimisticIdsRef.current.add(ticket.id);
      setTickets((prev) => {
        if (prev.some((t) => t.id === ticket.id)) return prev;
        return [ticket, ...prev];
      });
      fetchTickets();
      pollForCreated(ticket.id, ticket.subject);
    },
    [fetchTickets, pollForCreated],
  );

  React.useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Same RTA hook — but for the inbox list, so unread badges and last
  // message previews refresh when support replies on the other side.
  React.useEffect(() => {
    let alive = true;
    let eventName: string | null = null;
    const handler = () => {
      if (alive) fetchTickets();
    };
    sessionInfo().then((session) => {
      if (!alive || !session?.org_id) return;
      eventName = `supportTicketUpdate-${session.org_id}`;
      RTA.connect();
      RTA.on(eventName, handler);
    });
    return () => {
      alive = false;
      if (eventName) RTA.off(eventName, handler);
    };
  }, [fetchTickets]);

  return (
    <>
      {isFormOpen && (
        <SupportEmailForm onClose={() => setIsFormOpen(false)} onCreated={handleTicketCreated} />
      )}
      {openTicket && (
        <TicketThreadDrawer
          ticket={openTicket}
          onClose={() => {
            setOpenTicket(null);
            // Refetch on close so unread badges and last-message previews
            // reflect the seen-state stamped by the messages endpoint.
            fetchTickets();
          }}
          onReplied={fetchTickets}
        />
      )}
      <div id="pageSupportContainer">
        <div id="supportInboxHeader">
          <span id="supportInboxTitle">Suas conversas com o suporte</span>
          <Button
            icon={<FontAwesomeIcon icon={faPlus} />}
            iconSlot="start"
            class="supportNewTicketButton"
            callback={() => setIsFormOpen(true)}
          >
            Nova mensagem
          </Button>
        </div>

        <div id="supportInboxList">
          {loadingTickets ? (
            <SupportInboxSkeleton />
          ) : ticketsError ? (
            <SupportInboxError
              onRetry={() => {
                setLoadingTickets(true);
                fetchTickets();
              }}
            />
          ) : tickets.length === 0 ? (
            <SupportInboxEmpty onNewMessage={() => setIsFormOpen(true)} />
          ) : (
            tickets.map((t) => {
              // "temp-" rows are optimistic stubs whose real ticket is still
              // being minted async: not openable yet (opening 404s silently).
              const pending = t.id.startsWith("temp-");
              return (
                <TicketInboxRow
                  key={t.id}
                  ticket={t}
                  pending={pending}
                  onClick={() => {
                    if (!pending) setOpenTicket(t);
                  }}
                />
              );
            })
          )}
        </div>

        {!isMobile && (
          <div id="tutorialsListContainer">
            {Object.entries(courses).map(([key, course]) => (
              <TutorialSummary key={key} {...course} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
